import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  buildMetadataParamUpdates,
  readMetadataFilterParams,
} from '@/components/metadata-filters/urlParams';
import type { SupportedTimeZone } from '@/constants/date';
import { parseCallDirectionsParam } from '@/pages/qc/utils';
import { parseIdListParam, serializeMultiValueParam } from '@/utils/urlParams';

import { getDefaultDateRange } from '../utils/dateWindow';

export type AnalyticsTab = 'org' | 'agents';
const VALID_TABS = new Set<AnalyticsTab>(['org', 'agents']);

interface UseAnalyticsFiltersStateOptions {
  /** When true, fills in `start`/`end` defaults (last 7 days) on first mount if missing. */
  applyDateDefaults?: boolean;
  /** Dashboard-wide timezone from the store (no default — analytics must not fork one). */
  timeZone: SupportedTimeZone;
}

/**
 * Single owner of URL ↔ state for the analytics surface. Mirrors the pattern
 * used by QcContainer but scoped to the smaller analytics filter set.
 */
export const useAnalyticsFiltersState = ({
  applyDateDefaults = true,
  timeZone,
}: UseAnalyticsFiltersStateOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const tab: AnalyticsTab = VALID_TABS.has((tabParam || 'org') as AnalyticsTab)
    ? ((tabParam || 'org') as AnalyticsTab)
    : 'org';

  const programIds = useMemo(
    () => parseIdListParam(searchParams.get('program_ids')),
    [searchParams],
  );
  const callDirections = useMemo(
    () => parseCallDirectionsParam(searchParams.get('call_directions')),
    [searchParams],
  );
  const start = searchParams.get('start') || '';
  const end = searchParams.get('end') || '';

  // Per-org metadata advanced filters (AQ-496), mirrored to `metadata.<key>` URL
  // params for shareability. Keyed metadata key -> selected values.
  const metadata = useMemo(() => readMetadataFilterParams(searchParams), [searchParams]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value == null || value === '') params.delete(key);
            else params.set(key, value);
          });
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const defaultDateRange = useMemo(() => getDefaultDateRange(7, timeZone), [timeZone]);

  useEffect(() => {
    if (!applyDateDefaults) return;

    if (!start || !end) {
      updateSearchParams(defaultDateRange);
    }
  }, [applyDateDefaults, defaultDateRange, start, end, updateSearchParams]);

  const setTab = useCallback(
    (next: AnalyticsTab) => updateSearchParams({ tab: next === 'org' ? undefined : next }),
    [updateSearchParams],
  );

  const setProgramIds = useCallback(
    (next: string[]) => updateSearchParams({ program_ids: serializeMultiValueParam(next) }),
    [updateSearchParams],
  );

  const setCallDirections = useCallback(
    (next: string[]) =>
      updateSearchParams({ call_directions: serializeMultiValueParam(next), page: undefined }),
    [updateSearchParams],
  );

  const setDateRange = useCallback(
    (range: { start: string; end: string }) =>
      updateSearchParams({
        start: range.start || undefined,
        end: range.end || undefined,
        page: undefined,
      }),
    [updateSearchParams],
  );

  const setMetadataFilter = useCallback(
    (key: string, values: string[]) => {
      updateSearchParams({ ...buildMetadataParamUpdates({ [key]: values }), page: undefined });
    },
    [updateSearchParams],
  );

  return {
    tab,
    setTab,
    programIds,
    setProgramIds,
    callDirections,
    setCallDirections,
    start,
    end,
    setDateRange,
    metadata,
    setMetadataFilter,
    updateSearchParams,
  };
};
